<template>
  <div id="app">
    <div class="container">
      <!-- 左侧文件上传区域 -->
      <div class="upload-area">
        <div class="drop-zone">
          <el-upload
            ref="uploadRef"
            :limit="1"
            accept=".txt,.exe,.pdf,.docx,.png,.jpg,.jpeg,.mp4,.mp3"
            :auto-upload="false"
            drag
            :action="upload.url"
            :data="upload.data"
            :disabled="upload.isUploading"
            @change="handleFileChange"
            @progress="handleFileUploadProgress"
            @success="handleFileSuccess"
          >
            <template #trigger>
              <i class="el-icon-upload" />
              <div class="el-upload__text">
                将文件拖到此处，或 <em>点击上传</em>
              </div>
            </template>
            <div class="el-upload__tip" style="color: red">提示：支持导入 txt、exe、pdf、docx、png、jpg、mp4、mp3 等格式文件！(重新上传前，先关闭本条上传)</div>
          </el-upload>
        </div>
        <!-- 新增文件列表展示区域 -->
        <div class="file-list-area">
          <h3>文件列表</h3>
          <div class="table-container">
            <el-table
              :data="fileList"
              style="width: 100%"
              @row-click="handleRowClick"
              highlight-current-row
              :current-row-key="currentRowKey"
            >
              <el-table-column prop="fileName" label="文件名" />
              <el-table-column label="操作" width="120">
                <template #default="scope">
                  <el-button type="danger" size="small" @click="deleteFile(scope.row.fileName)">删除</el-button>
                </template>
              </el-table-column>
            </el-table>
          </div>
        </div>
      </div>
      <!-- 中间生成按钮 -->
      <div class="generate-button">
        <el-button @click="generateQRCode" type="primary">生成二维码</el-button>
      </div>
      <!-- 右侧二维码显示区域 -->
      <div class="qrcode-area">
        <!-- 展示二维码 -->
        <div v-if="showQrCode">
          <transition name="fade">
            <div class="qr-container">
              <!-- 包裹二维码组件 -->
              <div class="qr-code-wrapper">
                <MyQrCode :text="qrCodeUrl" :size="160" />
              </div>
              <!-- 新增按钮容器 -->
              <div class="button-container">
                <el-button @click="copyUrl" type="success">复制链接</el-button>
                <el-button @click="downloadFile" type="primary">下载</el-button>
              </div>
              <p v-if="copySuccess" class="copy-success-message">链接已复制到剪贴板</p>
              <p v-if="qrCodeUpdated" class="qr-code-updated-message">二维码已更新</p>
            </div>
          </transition>
        </div>
        <p v-else>上传文件并点击生成二维码</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { ElUpload, ElButton } from 'element-plus';
import MyQrCode from './components/MyQrCode.vue';
import axios from 'axios';

// 定义数据
const uploadRef = ref(null);
const upload = ref({
  isUploading: false,
  url: "/prod-api/file/upload",
  data: {}
});
const selectedFile = ref(null);
const fileList = ref([]);
const showQrCode = ref(false);
const qrCodeUrl = ref('');
const copySuccess = ref(false);
const currentRowKey = ref(null);
const qrCodeUpdated = ref(false);

// 获取文件列表
const getFileList = async () => {
  try {
    const response = await axios.get('/prod-api/file/list');
    fileList.value = response.data;
    // 假设新上传的文件在列表最后一个
    if (fileList.value.length > 0) {
      qrCodeUrl.value = fileList.value[0].fileUrl;
    }
  } catch (error) {
    console.error('获取文件列表失败:', error);
    alert('获取文件列表失败，请稍后重试');
  }
};

// 处理文件选择事件
const handleFileChange = (file) => {
  selectedFile.value = file.raw;
  if (uploadRef.value) {
    uploadRef.value.submit();
  }
};

// 处理文件上传进度事件
const handleFileUploadProgress = () => {
  upload.value.isUploading = true;
};

// 处理文件上传成功事件
const handleFileSuccess = () => {
  upload.value.isUploading = false;
  getFileList(); // 上传成功后刷新文件列表
  qrCodeUpdated.value = true;
  setTimeout(() => {
    qrCodeUpdated.value = false;
  }, 2000);
};

// 生成二维码
const generateQRCode = () => {
  if (qrCodeUrl.value) {
    showQrCode.value = true;
    copySuccess.value = false;
    qrCodeUpdated.value = true;
    setTimeout(() => {
      qrCodeUpdated.value = false;
    }, 2000);
  } else {
    alert('请先选择文件');
  }
};

// 复制链接
const copyUrl = async () => {
  try {
    await navigator.clipboard.writeText(qrCodeUrl.value);
    copySuccess.value = true;
    // 设置定时器，2 秒后隐藏提示信息
    setTimeout(() => {
      copySuccess.value = false;
    }, 2000);
  } catch (error) {
    console.error('复制链接失败:', error);
    alert('复制链接失败，请稍后重试');
  }
};

// 删除文件
const deleteFile = async (fileName) => {
  try {
    await axios.delete(`/prod-api/file/delete/${fileName}`);
    getFileList(); // 删除成功后刷新文件列表
  } catch (error) {
    console.error('删除文件失败:', error);
    alert('删除文件失败，请稍后重试');
  }
};

// 处理表格行点击事件
const handleRowClick = (row) => {
  qrCodeUrl.value = row.fileUrl;
  currentRowKey.value = row.fileName; // 假设 fileName 是唯一标识
  generateQRCode();
};

// 下载文件方法
const downloadFile = () => {
  if (qrCodeUrl.value) {
    const link = document.createElement('a');
    link.href = qrCodeUrl.value;
    link.download = qrCodeUrl.value.split('/').pop(); // 获取文件名
    link.click();
  } else {
    alert('请先选择文件');
  }
};

// 页面加载时获取文件列表
onMounted(() => {
  getFileList();
});
</script>

<style scoped>
/* 整体容器样式 */
.container {
  display: flex;
  justify-content: center; /* 使用 center 让元素居中并缩小间距 */
  align-items: flex-start; /* 使元素靠顶部对齐 */
  height: auto; /* 高度设置为自动 */
  margin-top: 20px; /* 设置距离顶部 20px */
  padding: 10px;
  background-color: #f9f9f9;
  gap: 20px; /* 设置元素之间的间距 */
}

/* 上传区域样式 */
.upload-area {
  width: 45%;
  border: 2px dashed #ccc;
  padding: 20px 10px;
  text-align: center;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
}

.drop-zone {
  cursor: pointer;
}

/* 文件列表区域样式 */
.file-list-area {
  margin-top: 10px;
}

.file-list-area h3 {
  margin: 5px 0;
  font-size: 16px;
}

/* 表格容器样式 */
.table-container {
  /* 移除 max-height 和 overflow-y 属性 */
}

/* 表格样式 */
::v-deep .el-table {
  font-size: 14px;
}

::v-deep .el-table th,
::v-deep .el-table td {
  padding: 5px;
}

/* 生成按钮区域样式 */
.generate-button {
  width: 10%;
  text-align: center;
  padding-top: 180px;
}

/* 二维码显示区域样式 */
.qrcode-area {
  width: 30%;
  text-align: center;
}

.qr-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background-color: #fff;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  width: 250px;
  height: 250px;
  margin: 0 auto;
}

.qr-code-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
}

/* 按钮容器样式 */
.button-container {
  display: flex;
  gap: 10px; /* 设置按钮之间的间距 */
}

/* 按钮样式优化 */
.el-button {
  border-radius: 4px;
  padding: 8px 16px;
  font-size: 14px;
}

/* 复制成功提示信息样式 */
.copy-success-message {
  color: green;
  font-size: 12px;
}

/* 选中行样式 */
::v-deep .el-table__body tr.current-row > td {
  background-color: #e5f6ff;
}

/* 二维码更新提示信息样式 */
.qr-code-updated-message {
  color: blue;
  font-size: 12px;
}

/* 过渡动画样式 */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>